import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Loader2, Star } from "lucide-react";
import { API } from "@/lib/api";

export default function ReviewSubmit() {
    const [params] = useSearchParams();
    const [ref] = useState(params.get("ref") || "");
    const [email] = useState(params.get("email") || "");
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!rating) {
            setError("Choisissez une note de 1 à 5 étoiles.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const r = await fetch(`${API}/reviews`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ref: ref.trim(), email: email.trim(), rating, comment, name }),
            });
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || "erreur");
            }
            setDone(true);
        } catch (err) {
            setError(
                err.message === "Avis déjà envoyé pour cette commande"
                    ? "Vous avez déjà laissé un avis pour cette commande. Merci !"
                    : "Avis non enregistré — vérifiez le lien reçu par e-mail."
            );
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-cream text-ink font-sans grid place-items-center px-4 py-16" data-testid="review-page">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-lg w-full rounded-[2rem] bg-white border border-sage/15 shadow-2xl shadow-sage/10 p-8 sm:p-12"
            >
                {done ? (
                    <div className="text-center" data-testid="review-success">
                        <span className="mx-auto w-20 h-20 rounded-full bg-sage text-cream grid place-items-center">
                            <CheckCircle2 size={36} strokeWidth={1.6} />
                        </span>
                        <h1 className="mt-8 font-serif text-3xl sm:text-4xl">Merci infiniment !</h1>
                        <p className="mt-4 text-base text-[#4A5550] leading-relaxed">
                            Votre avis vient d’être publié sur la boutique. Il aidera d’autres
                            personnes à retrouver le plaisir de s’asseoir sans douleur.
                        </p>
                        <Link
                            to="/"
                            data-testid="review-back-button"
                            className="mt-10 inline-flex rounded-full bg-sage hover:bg-sage-hover text-cream font-semibold px-8 py-4 transition-all hover:scale-[1.02]"
                        >
                            Retour à la boutique
                        </Link>
                    </div>
                ) : (
                    <>
                        <Link to="/" data-testid="review-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-sage hover:text-sage-hover transition-colors">
                            <ArrowLeft size={16} /> Boutique
                        </Link>
                        <h1 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight">Votre avis compte</h1>
                        <p className="mt-3 text-sm text-[#4A5550] leading-relaxed">
                            Votre coussin a-t-il transformé votre quotidien ? Partagez votre expérience
                            (commande {ref ? ref.slice(0, 18) + "…" : ""}) — cela aide les futurs clients.
                        </p>
                        <form data-testid="review-form" onSubmit={submit} className="mt-8 flex flex-col gap-5">
                            <div>
                                <label className="text-xs font-semibold text-ink/70 mb-2 block">Votre note</label>
                                <div className="flex gap-2" data-testid="review-stars">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            key={n}
                                            type="button"
                                            data-testid={`review-star-${n}`}
                                            onClick={() => setRating(n)}
                                            onMouseEnter={() => setHover(n)}
                                            onMouseLeave={() => setHover(0)}
                                            aria-label={`${n} étoiles`}
                                            className="p-1 transition-transform hover:scale-110"
                                        >
                                            <Star
                                                size={30}
                                                className={(hover || rating) >= n ? "text-terra" : "text-sand"}
                                                fill="currentColor"
                                                strokeWidth={0}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Votre prénom ou nom</label>
                                <input
                                    data-testid="review-field-name"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Camille B."
                                    className="w-full rounded-xl border border-sage/20 bg-cream px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-ink/70 mb-1.5 block">Votre expérience</label>
                                <textarea
                                    data-testid="review-field-comment"
                                    required
                                    minLength={5}
                                    maxLength={500}
                                    rows={4}
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Depuis que j’utilise mon Ergocox™…"
                                    className="w-full rounded-xl border border-sage/20 bg-cream px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 resize-none"
                                />
                            </div>
                            {error && (
                                <p data-testid="review-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                    {error}
                                </p>
                            )}
                            <button
                                data-testid="review-submit-button"
                                type="submit"
                                disabled={loading}
                                className="w-full rounded-full bg-sage hover:bg-sage-hover disabled:opacity-60 text-cream font-semibold py-4 flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                            >
                                {loading ? <Loader2 size={17} className="animate-spin" /> : null}
                                {loading ? "Envoi…" : "Publier mon avis"}
                            </button>
                        </form>
                    </>
                )}
            </motion.div>
        </div>
    );
}
